import type { UseQueryOptions, UseQueryResult } from '../react-query';
import { useQueryClient } from '../react-query';
import { fetchSSE } from '../utils/fetch-sse';
import useQuery from '../utils/useQuery';

export const SSE_QUERY_OPTIONS: Pick<UseQueryOptions, 'retry' | 'refetchOnWindowFocus'> = {
  retry: false, // Disable retry for SSE queries
  refetchOnWindowFocus: false, // Disable refetch on window focus for SSE queries
};

const useSSEQuerySubscription = <T = unknown, S = unknown>(
  queryOptions: UseQueryOptions,
  mergeFn: (oldData: S, data?: T) => S,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    ...queryOptions,
    ...SSE_QUERY_OPTIONS,
    // adding this because if we do not pass this then react query doesn't add the observer to render the data when the data changes
    // https://github.com/TanStack/query/blob/2283633684c90e1b22cb690c30949415fe5ddb52/packages/react-query/src/useBaseQuery.ts#L167
    // useBaseQuery.ts
    notifyOnChangeProps: 'all',
    queryFn: (queryArgs) =>
      // we are keeping the query function as the merger which directly update the query data
      // at the same time when the fetchSSE is complete, we return the merged data as it should be in case of normal useQuery which expects that in return of queryFn
      fetchSSE<T>(queryArgs, (data) => {
        // Update the existing query data with the new data received from SSE
        queryClient.setQueryData(queryArgs.queryKey, (oldData: S) => mergeFn(oldData, data));
      }).then(() => {
        return queryClient.getQueryData(queryArgs.queryKey);
      }),
  }) as UseQueryResult<S>;
};

export default useSSEQuerySubscription;
